/**
 * 화면 진입 가드·기본 진입 경로(메뉴 권한 §5 — FE UX 보조, 서버가 최종 차단).
 *
 * 권한·업무유형 수신 전에는 아무것도 렌더하지 않는다(게이트의 로딩 중 허용 폴백 때문에 생기는 깜빡임·잘못된 이동 방지).
 * 차단 시에는 다른 경로로 보내지 않고 그 자리에서 {@link NoPermission}을 보여 리다이렉트 루프를 만들지 않는다.
 */
import type { ReactNode } from "react";
import { Navigate, useParams } from "react-router-dom";

import { NoPermission } from "@/components/ui/NoPermission";
import {
  MENU_ACTION,
  type MENU_DASHBOARD_OVERALL,
  type MENU_DASHBOARD_PERSONAL,
} from "@/lib/dynamic/menuPermissions";
import { useMenuGate, useMenuPermsReady } from "@/lib/dynamic/useMenuGate";
import {
  firstDashboardPath,
  isNavTypeConfigured,
  NAV_PATH,
  useHomePath,
} from "@/lib/nav/sidebarModules";
import { useWorkitemTypes } from "@/lib/queries/useWorkitemTypes";
import { useWorkspaceDetail } from "@/lib/queries/useWorkspaces";

/**
 * 업무유형 화면(`w/:typeCode`, `w/:typeCode/:itemId`) 진입 가드.
 *  - 사이드바에 구성된 유형 → 그 유형의 조회(r) 권한(canType, 사이드바와 같은 규칙).
 *  - 사이드바에 없는 유형(숨김 유형·미구성 모듈 — 검색·알림·서명 이력 링크로 진입) → 별도 권한 줄이 없으므로
 *    모듈 조회 권한(canRead, §5.2 합집합 — 서버와 같은 기준).
 * 목록에 없는 유형 코드·유형 목록 조회 실패는 기존 화면 처리에 맡긴다.
 */
export function TypeMenuGuard({ children }: { children: ReactNode }) {
  const { workspace, typeCode } = useParams<{ workspace: string; typeCode: string }>();
  const { data: types, isError } = useWorkitemTypes(workspace);
  // 워크스페이스 조회 실패는 사이드바와 같이 미구성(전체 노출)으로 본다.
  const { data: wsDetail, isError: wsError } = useWorkspaceDetail(workspace);
  const gate = useMenuGate();
  const permsReady = useMenuPermsReady();

  if (!permsReady || (!types && !isError) || (!wsDetail && !wsError)) return null;
  const code = (typeCode ?? "").toUpperCase();
  const type = types?.find((wt) => wt.typeCode.toUpperCase() === code);
  if (!type) return <>{children}</>;
  const configured = isNavTypeConfigured(type, {
    modulesProvisioned: wsDetail?.modulesProvisioned ?? false,
    enabledModules: wsDetail?.enabledModules,
  });
  const allowed = configured
    ? gate.canType(type.typeCode, type.moduleCode, MENU_ACTION.READ)
    : gate.canRead(type.moduleCode);
  return allowed ? <>{children}</> : <NoPermission />;
}

/** 대시보드 경로 진입 가드 — 해당 대시보드 키의 조회(r) 권한(행 없으면 허용). */
export function DashboardMenuGuard({
  menuKey,
  children,
}: {
  menuKey: typeof MENU_DASHBOARD_PERSONAL | typeof MENU_DASHBOARD_OVERALL;
  children: ReactNode;
}) {
  const gate = useMenuGate();
  const permsReady = useMenuPermsReady();
  if (!permsReady) return null;
  return gate.canDashboard(menuKey) ? <>{children}</> : <NoPermission />;
}

/**
 * 구 단일 경로 `dashboard` → 보이는 첫 대시보드. 'dashboard' 라우트 내부라 부모(워크스페이스) 기준 `../` 로 보낸다.
 * 둘 다 숨으면 전체 대시보드로 보내 그 가드가 권한 없음을 보여 준다(루프 없음).
 */
export function DashboardIndexRedirect() {
  const gate = useMenuGate();
  const permsReady = useMenuPermsReady();
  if (!permsReady) return null;
  const target = firstDashboardPath(gate) ?? NAV_PATH.DASHBOARD_OVERALL;
  return <Navigate to={`../${target}`} replace />;
}

/** 워크스페이스 기본 진입(index) — 사이드바 첫 노출 메뉴로 이동, 노출 메뉴가 없으면 권한 없음 안내. */
export function HomeRedirect() {
  const { workspace } = useParams<{ workspace: string }>();
  const path = useHomePath(workspace);
  if (path === undefined) return null;
  if (path === null) return <NoPermission />;
  return <Navigate to={path} replace />;
}
